import { config } from "../assets/config";

const baseURL = config.VITE_API_BASE_URL;

let refreshingPromise = null;

// Optional: set this from your app so the client can logout nicely
let onLogout = () => {
  localStorage.clear();
  window.location.href = "/login";
};

export function setApiAuthHandlers({ logout }) {
  if (typeof logout === "function") onLogout = logout;
}

function getToken() {
  return localStorage.getItem("accessToken"); // e.g. "Bearer xyz"
}

function setToken(accessToken) {
  localStorage.setItem("accessToken", `Bearer ${accessToken}`);
}

async function refreshToken() {
  const token = getToken();

  const payloadToken = token.split(' ')[1];
  let existingToken = {
    token: payloadToken,
  }

  const resp = await fetch(`${baseURL}/api/auth/refresh_token`, {
    method: "POST", // match backend; change to POST if needed
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(existingToken),
  });

  if (!resp.ok) {
    // refresh failed → logout
    throw apiError(await safeJson(resp), resp.status);
  }

  const data = await resp.json();
  if (!data?.access_token) throw new Error("Missing access_token from refresh-token");
  setToken(data.access_token);
  return data.access_token;
}

async function safeJson(resp) {
  try {
    return await resp.json();
  } catch {
    return { status: resp.status, statusText: resp.statusText };
  }
}

// Consumers display Error.message. Keep diagnostics out of user-facing errors,
// including responses from proxies that are not JSON.
function apiError(body, status) {
  if (body instanceof Error) return body;
  let message;
  if (status === 401) {
    message = "Your session could not be authenticated. Please sign in again.";
  } else if (status >= 500) {
    message = "The service could not complete this request. Please try again. If it continues, contact support.";
  } else {
    const candidates = [body?.message, body?.msg, body?.error?.message, body?.error];
    message = candidates.find(value => typeof value === "string" && value.trim());
    if (!message) message = `Request failed (HTTP ${status}). Please try again.`;
  }
  const error = new Error(message);
  error.status = status;
  return error;
}

function shouldRefresh(status, body) {
  if (status !== 401) return false;

  // Mirror your Nuxt logic: refresh only for certain error messages
  const msg =
    body?.error?.message ||
    body?.error ||
    body?.message ||
    "";

  if (msg === "ApprovalError" || msg === "AuthenticationError" || msg === "AccessError") {
    return false; // don’t refresh, just fail
  }

  // Nuxt refreshes for ExpiredAccessError or any other 401 with errorMessage
  if (msg === "ExpiredAccessError") return true;
  if (msg) return true;

  // If backend gives plain 401 without message, you can decide:
  return true;
}

/**
 * apiFetch("/projects", { method: "GET" })
 */
export async function apiFetch(path, options = {}) {
  const url = path.startsWith("http") ? path : `${baseURL}${path}`;

  const isAuthRoute = url.includes("/auth/");
  const headers = new Headers(options.headers || {});

  if (!isAuthRoute) {
    const token = getToken();
    if (token) headers.set("Authorization", token);
  }

  const doRequest = async () => {
    const resp = await fetch(url, { ...options, headers });

    if (resp.ok) return resp;

    const body = await safeJson(resp);

    // If the refresh endpoint itself returns 401, logout (like your Nuxt code)
    if (url.endsWith("/auth/refresh_token") && resp.status === 401) {
      onLogout();
      throw apiError(body, resp.status);
    }

    // Try refresh?
    if (!isAuthRoute && shouldRefresh(resp.status, body)) {
      // single-flight refresh: everyone waits for the same promise
      if (!refreshingPromise) {
        refreshingPromise = refreshToken().finally(() => {
          refreshingPromise = null;
        });
      }

      try {
        const newAccessToken = await refreshingPromise;
        headers.set("Authorization", `Bearer ${newAccessToken}`);

        // retry original request once
        const retryResp = await fetch(url, { ...options, headers });
        if (retryResp.ok) return retryResp;

        // retry failed → treat as real failure
        const retryBody = await safeJson(retryResp);
        if (retryResp.status === 401) onLogout();
        throw apiError(retryBody, retryResp.status);
      } catch (e) {
        if (e?.name !== "AbortError") onLogout();
        throw e;
      }
    }

    // Not refreshable → throw body
    if (resp.status === 401) onLogout();
    throw apiError(body, resp.status);
  };

  return doRequest();
}
